import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Alert } from 'rsuite';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import ImageIcon from '@mui/icons-material/Image';

import { VpDropzone } from '@vp-ui';
import { useFetchUploadPresignedUrl, useUploadToPresignedUrl } from '@src/hooks';
import styles from '@src/components/CampaignLanding/ImageCollector.module.less';

const ACCEPTED_IMAGE_TYPES = {
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'image/gif': ['.gif'],
  'image/webp': ['.webp']
};

const MAX_FILE_SIZE = 50 * 1e6; // 50MB

const ImageCollector = ({
  value,
  onChange,
  required = true,
  organizationId,
  label = 'Upload Image',
  description = ''
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [isTouched, setIsTouched] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);

  const { mutateAsync: fetchUploadPresignedUrl } = useFetchUploadPresignedUrl();
  const { mutateAsync: uploadToPresignedUrl } = useUploadToPresignedUrl();

  // Clean up object URL when component unmounts or preview changes
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const hasFile = !!value?.attachment;
  const isValid = !required || hasFile;
  const showError = required && isTouched && !isValid && !isUploading;

  const onRemove = () => {
    setIsTouched(true);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    onChange({ attachment: null });
  };

  const onDropRejected = (rejectedFiles) => {
    const failReason = rejectedFiles[0]?.errors?.[0]?.message || 'File rejected';
    Alert.error(failReason, 7500);
    setIsUploading(false);
    setIsTouched(true);
  };

  const onDropAccepted = async (acceptedFiles) => {
    const file = acceptedFiles[0];
    let localPreviewUrl;
    try {
      setIsUploading(true);
      setIsTouched(true);

      // Create local preview URL immediately for better UX
      localPreviewUrl = URL.createObjectURL(file);
      setPreviewUrl(localPreviewUrl);

      const fileExtension = file.name.split('.').pop();

      const res = await fetchUploadPresignedUrl({
        mimeType: file.type,
        extension: fileExtension,
        asset_type: 'attachment',
        organizationId
      });

      const { presignedUrl } = res;
      const url = new URL(presignedUrl);
      const filePath = url.pathname.slice(1);

      await uploadToPresignedUrl({ presignedUrl, file });

      const attachment = {
        id: crypto.randomUUID(),
        filename: filePath.split('/')[1].split('.')[0],
        extension: fileExtension,
        path: filePath,
        size: file?.size,
        originalFilename: file?.name,
        contentType: file?.type
      };

      onChange({ attachment });
      setIsUploading(false);
    } catch (error) {
      Alert.error('Failed to upload image. Please try again.', 5000);
      // Clear preview on error
      if (localPreviewUrl) {
        URL.revokeObjectURL(localPreviewUrl);
        setPreviewUrl(null);
      }
      setIsUploading(false);
    }
  };

  return (
    <Box className={styles.vpImageCollectorContainer}>
      <Box className={styles.vpHeader}>
        <ImageIcon fontSize="small" color="primary" />
        <Typography variant="subtitle1" fontWeight={600} className={styles.vpLabel}>
          {label}
          {required && <span className={styles.vpRequiredLabel}>*</span>}
        </Typography>
      </Box>

      {description && (
        <section className={styles.vpDescription}>{description}</section>
      )}

      <Box className={styles.vpDropzoneWrapper}>
        <VpDropzone
          accept={ACCEPTED_IMAGE_TYPES}
          title="Click or drag an image to upload"
          onDropAccepted={onDropAccepted}
          onDropRejected={onDropRejected}
          multiple={false}
          maxFiles={1}
          maxSize={MAX_FILE_SIZE}
          onRemove={onRemove}
          hasFile={hasFile}
          disabled={isUploading}
          loading={isUploading}
          logoUrl={previewUrl}
        />
      </Box>

      {showError && (
        <Typography variant="caption" color="error" className={styles.vpErrorMessage}>
          Please upload an image
        </Typography>
      )}

      <Typography variant="caption" color="text.secondary" className={styles.vpHelpText}>
        Accepted formats: JPG, PNG, GIF, WebP (max 50MB)
      </Typography>
    </Box>
  );
};

ImageCollector.propTypes = {
  value: PropTypes.shape({
    attachment: PropTypes.object
  }),
  onChange: PropTypes.func.isRequired,
  required: PropTypes.bool,
  organizationId: PropTypes.string,
  label: PropTypes.string,
  description: PropTypes.string
};

export default ImageCollector;



// WEBPACK FOOTER //
// ./src/components/CampaignLanding/ImageCollector.jsx